import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Search, Tag } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { PriceItemDialog } from './price-item-dialog';
import { PRICE_LIST_QUERY_KEY, fetchPriceList, type PriceListItem } from './price-list-api';
import { useCanEdit } from '@/lib/auth';
import { LearnFromDocumentsDialog } from '@/features/learning/learn-dialog';

/* ---------------------------------------------------------------------------
   המחירון.

   עד עכשיו הוא נקבע באונבורדינג וקפא: בעל עסק שהעלה מחיר לא יכול היה
   לעדכן אותו, וכל חשבונית יצאה במחיר הישן.

   מחירון של עסק שירות הוא עשרות עד מאות שורות, ולכן הסינון כאן בצד
   הלקוח — חיפוש מיידי בלי בקשה על כל הקשה.
   --------------------------------------------------------------------------- */

type Dialog = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; item: PriceListItem };

export function PriceListPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['price-list', 'common']);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [dialog, setDialog] = useState<Dialog>({ mode: 'closed' });

  const list = useQuery({
    queryKey: [PRICE_LIST_QUERY_KEY, includeInactive],
    queryFn: ({ signal }) => fetchPriceList(includeInactive, signal),
  });

  const rows = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const all = list.data ?? [];
    if (!q) return all;
    return all.filter((i) => i.code.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
  }, [list.data, deferredSearch]);

  const openCreate = () => setDialog({ mode: 'create' });
  const columns = buildColumns(t, canEdit ? (item) => setDialog({ mode: 'edit', item }) : undefined);
  const hasAny = (list.data?.length ?? 0) > 0;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={
          canEdit ? (
            <div className="flex flex-wrap gap-2">
              <LearnFromDocumentsDialog />
              <Button size="sm" onClick={openCreate}>
                <Plus aria-hidden />
                {t('page.newItem')}
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:max-w-xs">
          <Search
            className="pointer-events-none absolute inset-y-0 start-3 my-auto size-4 text-fg-subtle"
            aria-hidden
          />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('page.searchPlaceholder')}
            aria-label={t('page.searchAria')}
            className="ps-9"
          />
        </div>
        <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-fg-muted">
          <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} aria-label={t('page.showInactiveAria')} />
          {t('page.showInactive')}
        </label>
      </div>

      <Card className="overflow-hidden">
        {list.isLoading ? (
          <TableSkeleton />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !hasAny ? (
          <EmptyState
            icon={Tag}
            title={t('page.emptyTitle')}
            description={t('page.emptyDescription')}
            action={
              canEdit ? (
                <div className="flex flex-wrap justify-center gap-2">
                  {/* מחירון ריק: הכי מהיר ללמוד אותו מהצעות מחיר וחשבוניות קיימות. */}
                  <LearnFromDocumentsDialog triggerVariant="primary" />
                  <Button size="sm" variant="secondary" onClick={openCreate}>
                    <Plus aria-hidden />
                    {t('page.newItem')}
                  </Button>
                </div>
              ) : undefined
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Search}
            title={t('page.notFoundTitle')}
            description={t('page.notFoundDescription', { term: deferredSearch.trim() })}
            action={
              <Button variant="secondary" size="sm" onClick={() => setSearch('')}>
                {t('page.clearSearch')}
              </Button>
            }
          />
        ) : (
          <>
            <DataTable caption={t('page.caption')} columns={columns} rows={rows} rowKey={(r) => r.id} />
            <p
              className="border-t border-border bg-surface-sunken/50 px-4 py-2.5 text-2xs text-fg-muted"
              aria-live="polite"
            >
              <span className="tabular">
                {t('page.count', { count: rows.length, formatted: formatNumber(rows.length) })}
              </span>
              {rows.length !== list.data?.length ? (
                <>
                  {' '}
                  {t('page.ofTotal')} <span className="tabular">{formatNumber(list.data?.length ?? 0)}</span>
                </>
              ) : null}
            </p>
          </>
        )}
      </Card>

      {dialog.mode !== 'closed' ? (
        <PriceItemDialog
          // מפתח לפי פריט: כל פתיחה מקבלת טופס נקי עם הערכים העדכניים.
          key={dialog.mode === 'edit' ? dialog.item.id : 'new'}
          item={dialog.mode === 'edit' ? dialog.item : null}
          onClose={() => setDialog({ mode: 'closed' })}
        />
      ) : null}
    </div>
  );
}

function buildColumns(
  t: TFunction<['price-list', 'common']>,
  onEdit?: (item: PriceListItem) => void,
): Array<Column<PriceListItem>> {
  const columns: Array<Column<PriceListItem>> = [
    {
      key: 'code',
      header: t('columns.code'),
      cell: (row) => (
        <span className={row.isActive ? 'ltr-inline font-medium text-fg' : 'ltr-inline text-fg-subtle line-through'}>
          {row.code}
        </span>
      ),
    },
    {
      key: 'description',
      header: t('columns.description'),
      cell: (row) => <span className={row.isActive ? 'text-fg' : 'text-fg-subtle'}>{row.description}</span>,
    },
    {
      key: 'price',
      header: t('columns.price'),
      numeric: true,
      // המחרוזת מוצגת כמות שהיא, דרך formatCurrency לתצוגה בלבד. היא
      // לעולם לא נשלחת חזרה אחרי שעברה דרך number.
      cell: (row) => <span className="tabular font-medium text-fg">{formatCurrency(row.price)}</span>,
    },
    {
      key: 'usedByTemplates',
      header: t('columns.usedByTemplates'),
      numeric: true,
      cell: (row) =>
        row.usedByTemplates === 0 ? (
          <span className="text-fg-subtle">—</span>
        ) : (
          <span className="tabular text-fg-muted">{formatNumber(row.usedByTemplates)}</span>
        ),
    },
    {
      key: 'status',
      header: t('columns.status'),
      cell: (row) =>
        row.isActive ? (
          <Badge tone="success">{t('columns.active')}</Badge>
        ) : (
          <Badge tone="neutral">{t('columns.inactive')}</Badge>
        ),
    },
  ];
  // בלי onEdit (צפייה בלבד) — בלי עמודת עריכה.
  if (!onEdit) return columns;
  return [
    ...columns,
    {
      key: 'actions',
      header: t('columns.actions'),
      className: 'text-end',
      cell: (row) => (
        <Button variant="ghost" size="icon" onClick={() => onEdit(row)} aria-label={t('columns.editAria', { code: row.code })}>
          <Pencil aria-hidden />
        </Button>
      ),
    },
  ];
}

function TableSkeleton() {
  return (
    <div className="divide-y divide-border">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3.5">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
      ))}
    </div>
  );
}
